import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    TextField,
    Grid,
    Button,
    Autocomplete,
    CircularProgress,
    IconButton,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemText,
    Paper,
    Box,
    InputAdornment,
    FormHelperText,
    Stack,
    Chip,
    Divider,
} from '@mui/material';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { createPaycodeDistribution, gettingCombinationRule } from 'utils/PaycodeDistribution';
import { gettinCombination } from 'utils/PaycodeDistribution';
import { nameValidation, normalizeString } from 'utils/Validations';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { AssignmentTable } from 'components/table/paycodeDistribution/AssignmentTable';
import CloseIcon from '@mui/icons-material/Close';

const PaperComponent = (props) => {
    const nodeRef = useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
};

const Add = ({ open, setOpen, reloading, rows }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [combinationRule, setCombinationRule] = useState([]);
    const [selectedCombinationRule, setSelectedCombinationRule] = useState([]);
    const [loadingCombinationRule, setLoadingCombinationRule] = useState(true);
    const [loadingTable, setLoadingTable] = useState(true);
    const [zones_desviations, setZonesDesviations] = useState([]);
    const [overtimes, setOvertimes] = useState([]);
    const [fullScreen, setFullScreen] = useState(false);
    const [crossCells, setCrossCells] = useState([]);
    const [paycodesDistribution, setPaycodesDistribution] = useState([]);
    const [loading, setloading] = useState(false);
    const [alerts, setAlerts] = useState(false);

    useEffect(() => {
        if (open) {
            gettingCombinationRule(setLoadingCombinationRule)
                .then((response) => {
                    setCombinationRule(response);
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoadingCombinationRule(false);
                });
        }

        setPaycodesDistribution([]);
    }, [open]);

    const closing = () => {
        setName('');
        setDescription('');
        setZonesDesviations([]);
        setOvertimes([]);
        setSelectedCombinationRule([]);
        setOpen(false);
        setAlerts(false);
    };

    const showtable = async (selectedCombinationRule) => {
        setSelectedCombinationRule(selectedCombinationRule);

        if (selectedCombinationRule) {
            gettinCombination(setLoadingTable, selectedCombinationRule)
                .then((response) => {
                    const combinationData = response?.data ?? response;
                    setZonesDesviations(combinationData?.zona_desviacion ?? []);
                    setOvertimes(combinationData?.overtimes ?? []);
                    setFullScreen(true);
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoadingTable(false);
                });
        }
    };

    const existName = (name) => {
        const normalizedInput = normalizeString(name);
        return rows.some((row) => normalizeString(row.name) === normalizedInput);
    };

    const changes = () => {
        const hasChanges = name !== '';
        return hasChanges ? setAlerts(true) : closing();
    };

    return (
        <>
            <Dialog
                fullWidth
                open={open}
                PaperComponent={PaperComponent}
                onClose={() => setOpen(false)}
                fullScreen={fullScreen}
                aria-labelledby="draggable-dialog-title"
                aria-describedby="draggable-alert-dialog-description"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    {t('settings.paycode_distribution.dialog.titles.add')}
                </DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            onClick={() => {
                                setFullScreen(!fullScreen);
                            }}
                            sx={{
                                position: 'absolute',
                                right: 50,
                                top: 8,
                                color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                        <IconButton
                            aria-label="close"
                            onClick={() => changes()}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ top: 0, zIndex: 10 }}>
                    {alerts && (
                        <Alert
                            variant="filled"
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <>
                                    <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.yes')}
                                    </Button>
                                    <Button onClick={() => closing()} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.no')}
                                    </Button>
                                </>
                            }
                        >
                            <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                        </Alert>
                    )}
                    <Divider />
                    <Stack direction="row" spacing={0.5} sx={{ mx: 3, my: 1 }}>
                        <Chip label={t('settings.paycode_distribution.dialog.inputs.add_table.columns.predetermined')} variant="outlined" size="medium" color="default" />
                        <Chip label={t('settings.submenu.zones')} variant="outlined" size="medium" color="primary" />
                        <Chip label={t('settings.submenu.schedule_deviation')} variant="outlined" size="medium" color="warning" />
                    </Stack>
                </Box>
                <DialogContent dividers>
                    {JSON.parse(localStorage.getItem('permissions')).filter((permission) =>
                        ['timekeeper.GetPaycodes', 'timekeeper.CombinationRules', 'timekeeper.CombinationRulesByIdDistribution'].includes(permission),
                    ).length !== 3 && (
                        <Alert severity="warning">
                            <AlertTitle>{t('general.alerts.missing_permission.title')}</AlertTitle>

                            <List dense>
                                {JSON.parse(localStorage.getItem('permissions')).find((permission) => permission === 'timekeeper.CombinationRules') === undefined && (
                                    <ListItem>
                                        <ListItemText primary={'timekeeper.CombinationRules'} />
                                    </ListItem>
                                )}

                                {JSON.parse(localStorage.getItem('permissions')).find((permission) => permission === 'timekeeper.GetPaycodes') === undefined && (
                                    <ListItem>
                                        <ListItemText primary={'timekeeper.GetPaycodes'} />
                                    </ListItem>
                                )}

                                {JSON.parse(localStorage.getItem('permissions')).find((permission) => permission === 'timekeeper.CombinationRulesByIdDistribution') === undefined && (
                                    <ListItem>
                                        <ListItemText primary={'timekeeper.CombinationRulesByIdDistribution'} />
                                    </ListItem>
                                )}
                            </List>
                        </Alert>
                    )}
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TextField
                                id={'input-name'}
                                name="name"
                                fullWidth
                                label={t('settings.paycode_distribution.dialog.inputs.name')}
                                value={name}
                                autoComplete="off"
                                onChange={(e) => {
                                    const newName = e.target.value;
                                    if (nameValidation(newName)) {
                                        setName(newName);
                                        existName(newName);
                                    }
                                }}
                                required
                                error={existName(name)}
                                slotProps={{
                                    htmlInput: { maxLength: 100 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${name.length}/100`}</InputAdornment>,
                                    },
                                }}
                            />
                            <FormHelperText
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 0.5,
                                    px: 1.75,
                                }}
                            >
                                <span>{existName(name) ? t('settings.schedule_desviation.table.dialog.add.exist_name') : ''}</span>
                            </FormHelperText>
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <Autocomplete
                                id="combination-rule-autocomplete"
                                name="combination_rule"
                                options={combinationRule?.length > 0 ? combinationRule : []}
                                onChange={(e, newValue) => showtable(newValue)}
                                getOptionLabel={(option) => `${option.name}`}
                                isOptionEqualToValue={(option, value) => option.combination_rule_id === value.combination_rule_id}
                                slotProps={{
                                    listbox: {
                                        sx: {
                                            maxHeight: 200,
                                            overflow: 'auto',
                                        },
                                    },
                                }}
                                fullWidth
                                loading={loadingCombinationRule}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id="combination-rule-input"
                                        name="combination_rule"
                                        autoComplete="off"
                                        label={t('settings.paycode_distribution.dialog.inputs.combination_rule')}
                                        required
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingCombinationRule ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                                noOptionsText={t('settings.paycode_distribution.dialog.inputs.autocomplete.no_options')}
                                loadingText={t('settings.paycode_distribution.dialog.inputs.autocomplete.loading')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                id="filled-textarea"
                                name="description"
                                placeholder={t('settings.paycode_distribution.dialog.inputs.max_description')}
                                fullWidth
                                label={t('settings.paycode_distribution.dialog.inputs.description')}
                                value={description}
                                autoComplete="off"
                                rows={2}
                                multiline
                                slotProps={{
                                    htmlInput: { maxLength: 255 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${description.length}/255`}</InputAdornment>,
                                    },
                                }}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Grid>

                        {loadingTable ? (
                            <></>
                        ) : (
                            <AssignmentTable
                                overtimes={overtimes}
                                zones_desviations={zones_desviations}
                                crossCells={crossCells}
                                setCrossCells={setCrossCells}
                                paycodesDistribution={paycodesDistribution}
                            />
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ mr: 1.5 }}>
                    <Button variant="contained" color="error" onClick={() => changes()}>
                        {t('general.buttons.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        loading={loading}
                        disabled={name === ''}
                        onClick={() => {
                            createPaycodeDistribution(setloading, name, description, selectedCombinationRule?.combination_rule_id, zones_desviations, overtimes, crossCells, closing, reloading)
                                .then((response) => {
                                    enqueueSnackbar(t('settings.paycode_distribution.dialog.alerts.add.success'), {
                                        variant: 'success',
                                        autoHideDuration: 3000,
                                    });
                                    closing();
                                    reloading();
                                })
                                .catch((error) => {
                                    console.error(error);
                                    enqueueSnackbar(t('settings.paycode_distribution.dialog.alerts.add.error'), {
                                        variant: 'error',
                                        autoHideDuration: 3000,
                                    });
                                });
                        }}
                    >
                        {t('general.buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { Add };
